// Badges (contracts/phase2.md §6, §12): the catalogue with rarity, holders and
// the share of active players holding each, with a medallion preview.

import { useSearchParams } from "react-router";
import { useBadges } from "../api/queries";
import { RARITIES, type BadgeRarity, type BadgeStat } from "../api/schemas";
import { Medallion, RARITY_TONE } from "../components/Medallion";
import { Chip, EmptyState, ErrorState, Field, Loading, PageHeader, Segmented, Select } from "../components/ui";
import { fmtInt } from "../lib/format";
import styles from "./pages.module.css";

type Sort = "catalogue" | "holders" | "rarest" | "name";
const RANK: Record<BadgeRarity, number> = { common: 0, uncommon: 1, rare: 2, epic: 3 };

function pct(v: number | null) {
  if (v === null) return "—";
  const p = v * 100;
  return `${p > 0 && p < 1 ? p.toFixed(1) : Math.round(p)}%`;
}

export function BadgesPage() {
  const q = useBadges();
  const [params, setParams] = useSearchParams();
  const rarity = (params.get("rarity") ?? "all") as BadgeRarity | "all";
  const sort = (params.get("sort") ?? "catalogue") as Sort;
  const update = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (!v || v === "all" || v === "catalogue") next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };

  const header = (
    <PageHeader
      title="Badges"
      meta={
        q.data ? (
          <span>
            {q.data.badges.length} badges · holders among {q.data.activePlayers !== null ? fmtInt(q.data.activePlayers) : "the"} players active in
            the last 30 days (cached hourly by the API).
          </span>
        ) : (
          <span>The badge catalogue with holder counts.</span>
        )
      }
    />
  );
  if (q.isPending)
    return (
      <>
        {header}
        <Loading rows={8} />
      </>
    );
  if (q.isError)
    return (
      <>
        {header}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );

  const rows = q.data.badges
    .map((b, i) => ({ b, i }))
    .filter(({ b }) => rarity === "all" || b.rarity === rarity)
    .sort((x, y) =>
      sort === "holders"
        ? y.b.holders - x.b.holders
        : sort === "rarest"
          ? x.b.holders - y.b.holders
          : sort === "name"
            ? x.b.name.localeCompare(y.b.name)
            : RANK[x.b.rarity] - RANK[y.b.rarity] || x.i - y.i,
    )
    .map(({ b }) => b);
  const counts = Object.fromEntries(RARITIES.map((r) => [r, q.data.badges.filter((b) => b.rarity === r).length]));

  return (
    <>
      {header}
      <div className={styles.filters}>
        <Segmented
          label="Rarity"
          value={rarity}
          onChange={(v) => update({ rarity: v })}
          options={[
            { value: "all", label: `All ${q.data.badges.length}` },
            ...RARITIES.map((r) => ({ value: r, label: `${r} ${counts[r] ?? 0}` })),
          ]}
        />
        <span className={styles.filtersEnd}>
          <Field label="Sort">
            <Select value={sort} onChange={(e) => update({ sort: e.target.value })} aria-label="Sort badges">
              <option value="catalogue">Rarity, catalogue order</option>
              <option value="holders">Most held</option>
              <option value="rarest">Least held</option>
              <option value="name">Name</option>
            </Select>
          </Field>
        </span>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No badges">No badge has that rarity.</EmptyState>
      ) : (
        <div className={styles.badgeGrid} data-testid="badge-grid">
          {rows.map((b) => (
            <BadgeCard key={b.id} badge={b} />
          ))}
        </div>
      )}
    </>
  );
}

function BadgeCard({ badge: b }: { badge: BadgeStat }) {
  return (
    <article className={styles.badgeCard} data-rarity={b.rarity} aria-label={`${b.name}, ${b.rarity}`}>
      <Medallion id={b.id} rarity={b.rarity} size={60} label={`${b.name} medallion`} />
      <div className={styles.badgeBody}>
        <div className={styles.badgeTitle}>
          <span>{b.name}</span>
          <Chip tone={RARITY_TONE[b.rarity]}>{b.rarity}</Chip>
          {b.hidden ? (
            <Chip tone="muted" title="Hidden until earned">
              hidden
            </Chip>
          ) : null}
        </div>
        <span className={styles.badgeDesc}>{b.description}</span>
        <span className={`mono ${styles.faint}`}>
          {b.id}
          {b.target ? ` · target ${b.target}` : ""}
        </span>
        <div className={styles.badgeHolders}>
          <span className={styles.meter} data-tone="accent" role="img" aria-label={`${pct(b.holderPct)} of active players`}>
            <span style={{ width: `${Math.min(100, (b.holderPct ?? 0) * 100)}%` }} />
          </span>
          <span className="mono" data-testid={`holders-${b.id}`}>
            {fmtInt(b.holders)}
          </span>
          <span className={`mono ${styles.faint}`}>{pct(b.holderPct)}</span>
        </div>
      </div>
    </article>
  );
}
